import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import IgnisIcon from '../components/icons/IgnisIcon';
import MapShell from '../components/map/MapShell';
import ValidacaoDayLayer from '../components/map/ValidacaoDayLayer';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { IGNIS_MODELS, STATE_DATE, fmtPct1 } from '../lib/siteState';
import {
  getValidacaoDay,
  getValidacaoSummary,
  SERIES_ORDER,
  type BandKind,
  type ValidacaoDayJson,
  type ValidacaoScorecards,
  type ValidacaoSeries,
  type ValidacaoSummary,
} from '../api/validacao';

// Validação v2: every section backed by the nightly bake on R2
// (validacao/summary.json + day-{date}.json). Metric definitions:
// docs/prod/VALIDACAO_PLAN.md. Prospective only: predictions were archived
// BEFORE the fires happened; no hindcasting.

type Cut = 'all' | 'ge1ha';

const fmtPct = (v: number | null | undefined, digits = 1): string =>
  v == null
    ? 'n/d'
    : `${(100 * v).toLocaleString('pt-PT', { maximumFractionDigits: digits })}%`;

const fmtRatio = (v: number | null | undefined): string =>
  v == null ? 'n/d' : `${v.toLocaleString('pt-PT', { maximumFractionDigits: 2 })}×`;

const fmtInt = (v: number): string => v.toLocaleString('pt-PT');

function Card({
  title,
  children,
  tag,
}: {
  title: string;
  children: React.ReactNode;
  tag?: string;
}) {
  return (
    <div className="rounded-xl border border-ink-700 bg-ink-900 p-5">
      <div className="flex items-baseline justify-between gap-2">
        <div className="font-mono text-[11px] uppercase tracking-wider text-mist-faint">{title}</div>
        {tag && (
          <div className="rounded-full border border-ink-700 bg-ink-800 px-2 py-0.5 font-mono text-[10px] text-mist-faint">
            {tag}
          </div>
        )}
      </div>
      {children}
    </div>
  );
}

// Hand-rolled SVG timeline (no chart lib, per the data-instrument style):
// bars = real fires per day (covered share in ember), line = flagged area %.
function Timeline({
  days,
  cut,
  showIpma,
  showEffis,
}: {
  days: ValidacaoSummary['days'];
  cut: Cut;
  showIpma: boolean;
  showEffis: boolean;
}) {
  const W = 860;
  const H = 250;
  const L = 46;
  const R = 12;
  const T = 12;
  const B = 46;
  const plotW = W - L - R;
  const plotH = H - T - B;

  const model = useMemo(() => {
    if (days.length === 0) return null;
    const dates = days.map((d) => d.date);
    const first = new Date(dates[0]);
    const last = new Date(dates[dates.length - 1]);
    const nDays = Math.round((last.getTime() - first.getTime()) / 86400000) + 1;
    const xOf = (date: string) =>
      L + ((new Date(date).getTime() - first.getTime()) / 86400000 / Math.max(nDays - 1, 1)) * plotW;

    const areaSeries = days.map((d) => d.flagged_frac.moderate_plus);
    const ipmaSeries = days.map((d) => (d.rcm.available ? d.rcm.level4_plus ?? null : null));
    const effisSeries = days.map((d) =>
      d.effis.available ? d.effis.area_frac?.very_high_plus ?? null : null,
    );
    let maxArea = Math.max(...areaSeries, 0.05);
    if (showIpma) maxArea = Math.max(maxArea, ...ipmaSeries.filter((v): v is number => v != null));
    if (showEffis) maxArea = Math.max(maxArea, ...effisSeries.filter((v): v is number => v != null));
    maxArea *= 1.12;
    const yOfArea = (v: number) => T + plotH - (v / maxArea) * plotH;

    const fires = days.map((d) => (cut === 'all' ? d.fires.matched : d.fires.ge1ha.matched));
    const covered = days.map((d) =>
      cut === 'all' ? d.fires.covered_moderate_plus : d.fires.ge1ha.covered_moderate_plus,
    );
    const maxFires = Math.max(...fires, 1);
    const barH = (v: number) => (v / maxFires) * (plotH * 0.45);
    const barW = Math.max(2, Math.min(10, (plotW / nDays) * 0.62));

    // Contiguous-day segments so archive gaps show as breaks, not fake lines.
    const segments = (series: (number | null)[]): string[] => {
      const paths: string[] = [];
      let cur: string[] = [];
      for (let i = 0; i < days.length; i++) {
        const v = series[i];
        const gap =
          i > 0 &&
          (new Date(days[i].date).getTime() - new Date(days[i - 1].date).getTime()) / 86400000 > 1;
        if (v == null || gap) {
          if (cur.length > 1) paths.push(`M${cur.join(' L')}`);
          cur = v == null ? [] : [`${xOf(days[i].date)},${yOfArea(v)}`];
          continue;
        }
        cur.push(`${xOf(days[i].date)},${yOfArea(v)}`);
      }
      if (cur.length > 1) paths.push(`M${cur.join(' L')}`);
      return paths;
    };

    const weekTicks = days.filter((_, i) => i % 7 === 0).map((d) => d.date);
    const yTicks = [0, maxArea / 2, maxArea];
    return {
      xOf, yOfArea, barH, barW, fires, covered, maxFires,
      areaPaths: segments(areaSeries),
      ipmaPaths: segments(ipmaSeries),
      effisPaths: segments(effisSeries),
      weekTicks, yTicks,
    };
  }, [days, cut, showIpma, showEffis, plotW, plotH]);

  if (!model) {
    return <div className="mt-4 h-64 rounded-xl border border-ink-700 bg-ink-900" />;
  }

  return (
    <div className="mt-4 overflow-x-auto rounded-xl border border-ink-700 bg-ink-900 p-4">
      <svg viewBox={`0 0 ${W} ${H}`} className="min-w-[640px]" role="img"
        aria-label="Série temporal: área sinalizada e fogos por dia">
        {model.yTicks.map((v) => (
          <g key={v}>
            <line x1={L} x2={W - R} y1={model.yOfArea(v)} y2={model.yOfArea(v)}
              stroke="#1e293b" strokeWidth="1" />
            <text x={L - 6} y={model.yOfArea(v) + 4} textAnchor="end"
              fontSize="11" fill="#64748b" fontFamily="monospace">
              {fmtPct(v, 0)}
            </text>
          </g>
        ))}
        {days.map((d, i) => {
          const x = model.xOf(d.date);
          const hTotal = model.barH(model.fires[i]);
          const hCov = model.barH(model.covered[i]);
          return (
            <g key={d.date}>
              <rect x={x - model.barW / 2} y={T + plotH - hTotal} width={model.barW}
                height={hTotal} fill="#475569" opacity="0.55" />
              <rect x={x - model.barW / 2} y={T + plotH - hCov} width={model.barW}
                height={hCov} fill="#f97316" opacity="0.9" />
            </g>
          );
        })}
        {model.areaPaths.map((p, i) => (
          <path key={`a${i}`} d={p} fill="none" stroke="#f97316" strokeWidth="2" />
        ))}
        {showIpma && model.ipmaPaths.map((p, i) => (
          <path key={`i${i}`} d={p} fill="none" stroke="#60a5fa" strokeWidth="1.8"
            strokeDasharray="5 4" />
        ))}
        {showEffis && model.effisPaths.map((p, i) => (
          <path key={`e${i}`} d={p} fill="none" stroke="#34d399" strokeWidth="1.8"
            strokeDasharray="2 4" />
        ))}
        {model.weekTicks.map((date) => (
          <text key={date} x={model.xOf(date)} y={H - 26} textAnchor="middle"
            fontSize="11" fill="#64748b" fontFamily="monospace">
            {date.slice(5)}
          </text>
        ))}
        <text x={L} y={H - 8} fontSize="11" fill="#64748b" fontFamily="monospace">
          barras: fogos/dia (laranja = em células Moderado+) · linha: % do território sinalizado
        </text>
      </svg>
    </div>
  );
}

function CalibrationTable({ sc, bandKind }: { sc: ValidacaoScorecards; bandKind: BandKind }) {
  if (!sc.calibration) return null;
  const rows = ['Low', 'Moderate', 'High', 'Extreme'] as const;
  const names: Record<string, string> = {
    Low: 'Baixo', Moderate: 'Moderado', High: 'Alto', Extreme: 'Extremo',
  };
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="border-b border-ink-700 font-mono text-[11px] uppercase tracking-wider text-mist-faint">
            <th className="py-2 pr-3">Nível</th>
            <th className="py-2 pr-3">{bandKind === 'share' ? 'Quota do território' : 'Banda nominal'}</th>
            <th className="py-2 pr-3">Frequência observada</th>
            <th className="py-2">Fogos (n)</th>
          </tr>
        </thead>
        <tbody className="text-mist-muted">
          {rows.map((lvl) => {
            const r = sc.calibration![lvl];
            if (!r) return null;
            return (
              <tr key={lvl} className="border-b border-ink-800">
                <td className="py-2 pr-3 text-mist">{names[lvl]}</td>
                <td className="py-2 pr-3 font-mono text-xs">
                  {fmtPct(r.band[0], 0)} – {fmtPct(r.band[1], 0)}
                </td>
                <td className="py-2 pr-3 font-mono text-xs">
                  {r.observed_rate == null ? 'n/d' : fmtPct(r.observed_rate, 3)}
                  <span className="text-mist-faint"> por célula-dia</span>
                </td>
                <td className="py-2 font-mono text-xs">{fmtInt(r.n_fire_cell_days)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function Validacao() {
  useDocumentMeta(
    'Validação: previsões vs fogos reais | Tutela IGNIS',
    'Avaliação prospetiva do IGNIS: previsões arquivadas antes dos fogos acontecerem, comparadas dia a dia com os incêndios reais e com os sistemas oficiais (IPMA RCM, EFFIS FWI).',
    '/ignis/validacao',
  );

  const [summary, setSummary] = useState<ValidacaoSummary | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [cut, setCut] = useState<Cut>('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [showIpma, setShowIpma] = useState(true);
  const [showEffis, setShowEffis] = useState(false);
  const [dayDate, setDayDate] = useState('');
  const [dayData, setDayData] = useState<ValidacaoDayJson | null>(null);
  const [showFirms, setShowFirms] = useState(false);
  const [seriesKey, setSeriesKey] = useState<string | null>(null);

  useEffect(() => {
    getValidacaoSummary()
      .then((s) => {
        setSummary(s);
        setSeriesKey(s.served_series ?? null);
        setFrom(s.window.start);
        setTo(s.window.end);
        const last = s.days[s.days.length - 1];
        if (last) setDayDate(last.date);
      })
      .catch(() => setLoadError(true));
  }, []);

  // The series on screen: one per served model family when the bake publishes them
  // (O5), else the single legacy document. The top level of summary.json is always the
  // served series, so a summary without `series` still renders.
  const seriesList: ValidacaoSeries[] = useMemo(() => {
    if (!summary?.series) return [];
    return SERIES_ORDER.filter((k) => summary.series![k]).map((k) => summary.series![k]);
  }, [summary]);
  const view: ValidacaoSeries | null = useMemo(() => {
    if (!summary) return null;
    if (seriesKey && summary.series?.[seriesKey]) return summary.series[seriesKey];
    return summary;
  }, [summary, seriesKey]);
  const bandKind: BandKind = view?.band_kind ?? 'probability';

  const selectSeries = (key: string) => {
    if (!summary?.series?.[key]) return;
    const s = summary.series[key];
    setSeriesKey(key);
    setFrom(s.window.start);
    setTo(s.window.end);
  };

  useEffect(() => {
    if (!dayDate) return;
    let cancelled = false;
    setDayData(null);
    getValidacaoDay(dayDate)
      .then((d) => { if (!cancelled) setDayData(d); })
      .catch(() => { if (!cancelled) setDayData(null); });
    return () => { cancelled = true; };
  }, [dayDate]);

  const daysInRange = useMemo(
    () => (view ? view.days.filter((d) => d.date >= from && d.date <= to) : []),
    [view, from, to],
  );

  // Coverage + misses are summable per day, so they honour the date range;
  // the vs-IPMA/vs-EFFIS/calibration cards are whole-window (labelled).
  const range = useMemo(() => {
    const pick = (d: ValidacaoSummary['days'][number]) =>
      cut === 'all'
        ? { n: d.fires.matched, cm: d.fires.covered_moderate_plus, ch: d.fires.covered_high_plus, miss: d.fires.missed_low }
        : { n: d.fires.ge1ha.matched, cm: d.fires.ge1ha.covered_moderate_plus, ch: d.fires.ge1ha.covered_high_plus, miss: d.fires.ge1ha.missed_low };
    let n = 0, cm = 0, ch = 0, miss = 0, area = 0, areaH = 0;
    for (const d of daysInRange) {
      const p = pick(d);
      n += p.n; cm += p.cm; ch += p.ch; miss += p.miss;
      area += d.flagged_frac.moderate_plus;
      areaH += d.flagged_frac.high_plus;
    }
    const days = Math.max(daysInRange.length, 1);
    return { n, cm, ch, miss, areaMean: area / days, areaHighMean: areaH / days };
  }, [daysInRange, cut]);

  const sc: ValidacaoScorecards | null = view
    ? cut === 'all' ? view.scorecards : view.scorecards_ge1ha
    : null;

  // The day map is always the map the public saw that morning (the served family), so
  // the dates come from the served series, whichever series the cards show.
  const availableDayDates = useMemo(
    () => (summary ? summary.days.map((d) => d.date).reverse() : []),
    [summary],
  );

  const dataStreams: { name: string; status: string; ok: boolean }[] = [
    {
      name: 'Previsões IGNIS',
      status:
        'Arquivadas diariamente desde 19 de maio de 2026, antes de os fogos acontecerem. 5 dias sem previsão (falhas de produção, permanentes) estão identificados e contam como lacunas.',
      ok: true,
    },
    {
      name: 'Fogos reais (ICNF + satélite)',
      status: 'Arquivados diariamente desde 19 de maio de 2026 (ocorrências ICNF + deteções FIRMS), com 2 dias de maturação.',
      ok: true,
    },
    {
      name: 'Previsões oficiais (IPMA RCM)',
      status: 'Arquivadas diariamente desde 4 de junho de 2026, três vezes por dia. A comparação vs IPMA começa nessa data.',
      ok: true,
    },
    {
      name: 'EFFIS FWI (Copernicus)',
      status: 'Reanálise diária ~25 km desde 19 de maio de 2026, com ~5 dias de atraso de publicação. Dias ainda não publicados ficam ausentes, nunca a zero.',
      ok: true,
    },
  ];

  if (loadError) {
    return (
      <div className="min-h-[calc(100vh-4rem)] bg-ink-950 text-mist">
        <div className="mx-auto max-w-6xl px-6 py-16">
          <h1 className="font-display text-3xl font-bold">Validação</h1>
          <p className="mt-4 text-mist-muted">
            Não foi possível carregar os dados de validação. Tenta novamente daqui a uns minutos.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-ink-950 text-mist">
      <div className="mx-auto max-w-6xl px-6 py-16">
        {/* Header */}
        <div className="mb-6 flex flex-wrap items-center gap-3">
          <div className="inline-flex items-center gap-2 rounded-full border border-ember/30 bg-ember/10 px-4 py-1.5 font-mono text-sm font-semibold text-ember">
            <IgnisIcon className="h-4 w-4" />
            IGNIS · Validação
          </div>
          <span className="rounded-full border border-ink-700 bg-ink-800 px-3 py-1 font-mono text-[11px] uppercase tracking-wider text-mist-muted">
            Avaliação prospetiva
          </span>
        </div>

        <h1 className="font-display text-3xl font-bold tracking-tight text-mist sm:text-4xl">
          As previsões, comparadas com a realidade
        </h1>
        <p className="mt-6 max-w-2xl text-lg leading-relaxed text-mist-muted">
          Desde 19 de maio de 2026, o mapa de risco de cada manhã fica arquivado antes de qualquer
          fogo acontecer. Esta página compara essas previsões, dia a dia, com os incêndios que
          realmente ocorreram e com os sistemas oficiais (IPMA e EFFIS). Incluindo os dias em que
          falhámos.
        </p>
        <p className="mt-4 font-mono text-sm text-mist-muted">
          Dados públicos e versionados diariamente ·{' '}
          <a
            href="https://github.com/neuralforge-pt/tutela-ignis"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-ember hover:underline"
          >
            Arquivo público de dados →
          </a>
        </p>

        {summary && view && (
          <>
            {seriesList.length <= 1 && (
              <div className="mt-8 rounded-xl border border-ink-700 bg-ink-900/70 p-5 text-sm leading-relaxed text-mist-muted">
                <div className="mb-1.5 font-mono text-[11px] uppercase tracking-wider text-ember">
                  Em sombra · estado em {STATE_DATE}
                </div>
                Esta página avalia o modelo em serviço ({IGNIS_MODELS.served.version}). Desde{' '}
                {IGNIS_MODELS.rebuilt.shadowSince} um modelo reconstruído emite um mapa todas as manhãs em
                paralelo, sem entrar no mapa público, e é pontuado contra as mesmas ignições: nos{' '}
                {IGNIS_MODELS.rebuilt.shadow.liveDays} dias ao vivo já pontuados ({fmtInt(IGNIS_MODELS.rebuilt.shadow.liveFires)} ignições)
                os 5% de células sinalizadas cobriram {fmtPct1(IGNIS_MODELS.rebuilt.shadow.live.served)} das ignições no
                modelo em serviço, {fmtPct1(IGNIS_MODELS.rebuilt.shadow.live.lookup)} na tabela histórica por célula e mês e{' '}
                {fmtPct1(IGNIS_MODELS.rebuilt.shadow.live.rebuilt)} no modelo reconstruído. A troca só acontece se o portão
                público ({IGNIS_MODELS.rebuilt.gate.liveDays} dias, {fmtInt(IGNIS_MODELS.rebuilt.gate.fires)} ignições) passar;
                nesse dia esta série fecha e a do modelo novo começa ao lado. Detalhe em{' '}
                <Link to="/ignis/modelos" className="text-ember/80 underline-offset-2 hover:underline">
                  Modelos
                </Link>.
              </div>
            )}

            {/* Controls */}
            <div className="mt-10 rounded-xl border border-ink-700 bg-ink-900 p-6">
              {seriesList.length > 1 && (
                <div className="mb-5">
                  <div className="mb-1.5 font-mono text-[11px] uppercase tracking-wider text-mist-faint">
                    Série avaliada
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {seriesList.map((s) => (
                      <button
                        key={s.family} type="button" onClick={() => selectSeries(s.family)}
                        className={`rounded-lg border px-3 py-2 text-sm transition-colors ${
                          view.family === s.family
                            ? 'border-ember/50 bg-ember/15 text-ember'
                            : 'border-ink-700 bg-ink-800/60 text-mist-muted hover:text-mist'
                        }`}
                      >
                        {s.label}
                        <span className="ml-2 font-mono text-[11px] text-mist-faint">
                          {s.window.start.slice(5)} → {s.window.end.slice(5)}
                        </span>
                      </button>
                    ))}
                  </div>
                  <p className="mt-2 text-xs leading-snug text-mist-faint">
                    {summary.switch_date
                      ? `Troca de modelo em ${summary.switch_date}: a série do modelo anterior fecha nesse dia e a do modelo reconstruído começa aí. `
                      : ''}
                    A referência é a taxa histórica de ignições de cada célula no mesmo mês, sem
                    meteorologia: qualquer modelo tem de a bater para ter mérito. Cada série é avaliada
                    com as suas próprias previsões arquivadas, nos mesmos dias e contra os mesmos fogos.
                  </p>
                </div>
              )}
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div>
                  <div className="mb-1.5 font-mono text-[11px] uppercase tracking-wider text-mist-faint">De</div>
                  <input
                    type="date" value={from}
                    min={view.window.start} max={to}
                    onChange={(e) => setFrom(e.target.value)}
                    className="w-full rounded-lg border border-ink-700 bg-ink-800/60 px-3 py-2 text-sm text-mist"
                  />
                </div>
                <div>
                  <div className="mb-1.5 font-mono text-[11px] uppercase tracking-wider text-mist-faint">Até</div>
                  <input
                    type="date" value={to}
                    min={from} max={view.window.end}
                    onChange={(e) => setTo(e.target.value)}
                    className="w-full rounded-lg border border-ink-700 bg-ink-800/60 px-3 py-2 text-sm text-mist"
                  />
                </div>
                <div className="lg:col-span-2">
                  <div className="mb-1.5 font-mono text-[11px] uppercase tracking-wider text-mist-faint">
                    Fogos considerados
                  </div>
                  <div className="flex gap-2">
                    {([
                      ['all', `Todas as ignições (${fmtInt(view.scorecards.coverage.moderate_plus.fires_total)})`],
                      ['ge1ha', `Fogos ≥ 1 ha (${fmtInt(view.scorecards_ge1ha.coverage.moderate_plus.fires_total)})`],
                    ] as [Cut, string][]).map(([value, label]) => (
                      <button
                        key={value} type="button" onClick={() => setCut(value)}
                        className={`rounded-lg border px-3 py-2 text-sm transition-colors ${
                          cut === value
                            ? 'border-ember/50 bg-ember/15 text-ember'
                            : 'border-ink-700 bg-ink-800/60 text-mist-muted hover:text-mist'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <p className="mt-3 text-xs leading-snug text-mist-faint">
                As duas contagens são sempre publicadas lado a lado: todas as ignições ICNF (incluindo
                queimadas pequenas, a tarefa mais difícil) e os fogos que atingiram 1 ha. Áreas
                registadas a D+2 podem subestimar fogos ainda ativos.
              </p>
            </div>

            {/* Timeline */}
            <div className="mt-10">
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                  <h2 className="font-display text-lg font-bold text-mist">Linha temporal</h2>
                  <p className="mt-1 text-sm text-mist-muted">
                    Área sinalizada (Moderado+) vs fogos reais, dia a dia. Interrupções = dias sem previsão arquivada.
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button" onClick={() => setShowIpma((v) => !v)}
                    className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                      showIpma ? 'border-sky-400/50 bg-sky-400/10 text-sky-300' : 'border-ink-700 text-mist-faint'
                    }`}
                  >
                    IPMA RCM ≥ 4 (área)
                  </button>
                  <button
                    type="button" onClick={() => setShowEffis((v) => !v)}
                    className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                      showEffis ? 'border-emerald-400/50 bg-emerald-400/10 text-emerald-300' : 'border-ink-700 text-mist-faint'
                    }`}
                  >
                    EFFIS FWI ≥ 38 (área)
                  </button>
                </div>
              </div>
              <Timeline days={daysInRange} cut={cut} showIpma={showIpma} showEffis={showEffis} />
            </div>

            {/* Scorecards */}
            <div className="mt-10">
              <h2 className="font-display text-lg font-bold text-mist">Desempenho</h2>
              <p className="mt-1 text-sm text-mist-muted">
                Cobertura sempre apresentada ao lado da área sinalizada: sinalizar o país inteiro
                daria 100% de cobertura e não teria mérito nenhum.
              </p>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <Card title="Cobertura de fogos" tag={`${from.slice(5)} → ${to.slice(5)}`}>
                  <div className="mt-2 font-display text-3xl font-bold text-ember">
                    {range.n ? fmtPct(range.cm / range.n) : 'n/d'}
                  </div>
                  <div className="mt-1 text-sm leading-snug text-mist-muted">
                    dos fogos ({fmtInt(range.cm)} de {fmtInt(range.n)}) começaram em células
                    Moderado+, sinalizando em média{' '}
                    <span className="text-mist">{fmtPct(range.areaMean)}</span> do território.
                  </div>
                  <div className="mt-2 text-xs text-mist-faint">
                    Alto+: {range.n ? fmtPct(range.ch / range.n) : 'n/d'} dos fogos com {fmtPct(range.areaHighMean)} do território.
                  </div>
                </Card>
                <Card title="Falhas (o número que ninguém publica)" tag={`${from.slice(5)} → ${to.slice(5)}`}>
                  <div className="mt-2 font-display text-3xl font-bold text-red-400">
                    {fmtInt(range.miss)}
                  </div>
                  <div className="mt-1 text-sm leading-snug text-mist-muted">
                    de {fmtInt(range.n)} fogos começaram em células que nessa manhã estavam marcadas
                    Baixo. Estão assinalados a vermelho no mapa do dia.
                  </div>
                </Card>
                <Card title="vs IPMA (RCM)" tag={`janela completa · ${fmtInt(sc!.vs_ipma.n_fires)} fogos`}>
                  <div className="mt-2 font-display text-3xl font-bold text-ember">
                    {fmtRatio(sc!.vs_ipma.level4_plus?.area_ratio)}
                  </div>
                  <div className="mt-1 text-sm leading-snug text-mist-muted">
                    menos área sinalizada para a mesma cobertura de fogos que o aviso oficial de
                    nível Muito Elevado+ ({fmtPct(sc!.vs_ipma.level4_plus?.coverage)} dos fogos,{' '}
                    {fmtPct(sc!.vs_ipma.level4_plus?.area_frac)} do território).
                  </div>
                  <div className="mt-2 text-xs text-mist-faint">
                    Nível Máximo: {fmtRatio(sc!.vs_ipma.level5?.area_ratio)} menos área. Desde 04-06-2026.
                  </div>
                </Card>
                <Card title="vs EFFIS (FWI)" tag={`janela completa · ${fmtInt(sc!.vs_effis.n_fires)} fogos`}>
                  <div className="mt-2 font-display text-3xl font-bold text-ember">
                    {fmtRatio(sc!.vs_effis.very_high_plus?.area_ratio)}
                  </div>
                  <div className="mt-1 text-sm leading-snug text-mist-muted">
                    menos área para a mesma cobertura que o índice europeu em classe Muito Alta+
                    (FWI ≥ 38). Em classe Alta+ (FWI ≥ 21,3): {fmtRatio(sc!.vs_effis.high_plus?.area_ratio)}.
                  </div>
                  <div className="mt-2 text-xs text-mist-faint">
                    Reanálise ~25 km; comparação no ponto de ignição.
                  </div>
                </Card>
              </div>
              <p className="mt-3 text-xs leading-snug text-mist-faint">
                O IPMA e o EFFIS preveem perigo de incêndio (comportamento do fogo se algo arder), não
                o local de ignição. As comparações acima medem uma tarefa diferente, onde a resolução
                de 1 km e os fatores humanos do IGNIS contam. A medida do modelo (ignições cobertas
                pelos 5% de células sinalizadas; a AUC de 0,83 publicada em 2026 foi retirada) está
                explicada em{' '}
                <Link to="/ignis/modelos" className="text-ember/80 underline-offset-2 hover:underline">
                  Modelos
                </Link>.
              </p>
            </div>

            {/* Day map + calibration */}
            <div className="mt-10 grid gap-8 lg:grid-cols-2">
              <div>
                <h2 className="font-display text-lg font-bold text-mist">Mapa do dia</h2>
                <p className="mt-1 text-sm text-mist-muted">
                  O mapa daquela manhã (células Moderado+), com as ignições reais desse dia por cima.
                </p>
                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <select
                    value={dayDate}
                    onChange={(e) => setDayDate(e.target.value)}
                    className="rounded-lg border border-ink-700 bg-ink-800/60 px-3 py-2 text-sm text-mist"
                  >
                    {availableDayDates.map((d) => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                  <label className="flex items-center gap-2 text-xs text-mist-muted">
                    <input
                      type="checkbox" checked={showFirms}
                      onChange={(e) => setShowFirms(e.target.checked)}
                      className="accent-ember"
                    />
                    deteções de satélite (FIRMS)
                  </label>
                </div>
                <div className="mt-3 h-96 overflow-hidden rounded-xl border border-ink-700">
                  <MapShell
                    tileUrl="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                    tileAttribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
                  >
                    {dayData && <ValidacaoDayLayer day={dayData} showFirms={showFirms} />}
                  </MapShell>
                </div>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-mist-faint">
                  <span>● círculo cheio = ignição real (cor = nível da célula nessa manhã)</span>
                  <span className="text-red-400">● contorno vermelho = falha (célula Baixo)</span>
                  {dayData && (
                    <span>
                      {fmtInt(dayData.fires.length)} ignições ·{' '}
                      {fmtInt(dayData.fires.filter((f) => f.level === 'Low' || f.level == null).length)} falhas
                    </span>
                  )}
                </div>
              </div>

              <div>
                <h2 className="font-display text-lg font-bold text-mist">Calibração por nível</h2>
                <p className="mt-1 text-sm text-mist-muted">
                  Com que frequência ardeu, de facto, uma célula de cada nível (todas as ignições,
                  janela completa).
                </p>
                <div className="mt-4 rounded-xl border border-ink-700 bg-ink-900 p-5">
                  <CalibrationTable sc={view.scorecards} bandKind={bandKind} />
                  {bandKind === 'share' ? (
                    <p className="mt-3 text-xs leading-snug text-mist-faint">
                      Nesta série os níveis são quotas fixas do território por dia (Extremo 1%,
                      Alto 2%, Moderado 5%), atribuídas por ordenação. A frequência observada é a
                      taxa real de ignição em cada quota: se os níveis ordenam bem o risco, sobe de
                      Baixo para Extremo.
                    </p>
                  ) : (
                    <p className="mt-3 text-xs leading-snug text-mist-faint">
                      Leitura honesta: os níveis ordenam bem o risco relativo, mas a frequência
                      observada por célula-dia fica muito abaixo das bandas nominais. O valor do mapa
                      deve ler-se como pontuação relativa de risco, não como probabilidade literal de
                      uma célula arder nesse dia. Estamos a medir isto publicamente para o corrigir.
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Como medimos */}
            <details className="group mt-10 rounded-xl border border-ink-700 bg-ink-900 p-6">
              <summary className="cursor-pointer font-display text-lg font-bold text-mist marker:text-ember">
                Como medimos
              </summary>
              <div className="mt-4 space-y-3 text-sm leading-relaxed text-mist-muted">
                <p>
                  <span className="text-mist">Verdade:</span> ocorrências ICNF sem falso alarme,
                  associadas à célula de 1 km mais próxima (máx. 1,5 km). As deteções FIRMS são
                  secundárias (mostram píxeis a arder, não ignições). Previsão avaliada = o ciclo
                  das 09:30 de cada dia, o mapa que qualquer pessoa viu nessa manhã.
                </p>
                <p>
                  <span className="text-mist">Cobertura emparelhada:</span> nunca publicamos cobertura
                  sem a área sinalizada ao lado. Um sistema que pinte o país inteiro cobre 100% dos
                  fogos e não presta.
                </p>
                <p>
                  <span className="text-mist">Comparação a cobertura igualada:</span> como o RCM de
                  verão marca quase o país todo com níveis altos, comparamos no ponto de operação:
                  fixamos a cobertura de fogos que o sistema oficial atingiu e medimos quanta área
                  cada um precisou de sinalizar para lá chegar.
                </p>
                <p>
                  <span className="text-mist">Índices de perícia:</span> TSS e ETS dão zero a quem
                  sinalize tudo. Janela completa, célula-dia, Moderado+:{' '}
                  <span className="font-mono text-xs">
                    TSS {sc!.skill.moderate_plus.tss.toLocaleString('pt-PT')} · ETS {sc!.skill.moderate_plus.ets.toLocaleString('pt-PT')}
                  </span>
                  {' '}(valores pequenos são normais para eventos raros: a taxa-base é ~0,05%).
                </p>
                <p>
                  <span className="text-mist">Proveniência:</span> cada previsão é registada
                  publicamente antes de o resultado ser conhecido, num histórico Git não editável (a
                  previsão de amanhã fica gravada hoje). O arquivo diário completo (as nossas
                  previsões, os produtos oficiais e os fogos reais) é aberto em{' '}
                  <a
                    href="https://github.com/neuralforge-pt/tutela-ignis"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-ember hover:underline"
                  >
                    github.com/neuralforge-pt/tutela-ignis
                  </a>.
                </p>
                <p className="text-mist-faint">
                  Limitações conhecidas: 5 dias sem previsão arquivada (contados como lacunas, nunca
                  preenchidos); vs IPMA só desde 04-06-2026; EFFIS com ~5 dias de atraso e ~25 km de
                  resolução; áreas ICNF registadas a D+2 podem subestimar fogos ainda ativos; a área
                  do RCM é aproximada por proximidade ao centróide de cada concelho até integrarmos
                  os limites administrativos oficiais; n pequeno na janela inicial. Os dados brutos
                  desta página são públicos em{' '}
                  <span className="font-mono text-xs">data.tutela.land/validacao/summary.json</span>.
                </p>
              </div>
            </details>

            {/* Data collection status */}
            <div className="mt-12">
              <h2 className="font-display text-lg font-bold text-mist">Estado da recolha de dados</h2>
              <p className="mt-1 text-sm text-mist-muted">
                Quatro séries diárias emparelhadas por data. Última atualização do arquivo:{' '}
                {summary.window.end} ({fmtInt(view.window.days_evaluated)} dias avaliados na série
                em ecrã).
              </p>
              <div className="mt-4 space-y-3">
                {dataStreams.map(({ name, status, ok }) => (
                  <div key={name} className="flex items-start gap-3 rounded-xl border border-ink-700 bg-ink-900 p-4">
                    <div className={`mt-1 h-2.5 w-2.5 flex-shrink-0 rounded-full ${ok ? 'bg-emerald-400' : 'bg-amber-400'}`} />
                    <div>
                      <div className="text-sm font-medium text-mist">{name}</div>
                      <div className="mt-0.5 text-xs leading-snug text-mist-muted">{status}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {!summary && !loadError && (
          <div className="mt-10 h-64 animate-pulse rounded-xl border border-ink-700 bg-ink-900" />
        )}

        {/* Note */}
        <div className="mt-12 rounded-xl border border-ember/30 bg-ember/5 p-6">
          <div className="font-mono text-xs uppercase tracking-[0.2em] text-ember">Compromisso</div>
          <p className="mt-3 leading-relaxed text-mist-muted">
            A avaliação é sempre contra o que realmente ardeu, não contra números fáceis. Esta página
            mostra a comparação dia a dia, incluindo os dias em que falhámos: os fogos que começaram
            em células Baixo estão contados no cartão de falhas e marcados a vermelho no mapa.
          </p>
        </div>

        <div className="mt-10 border-t border-ink-800 pt-8">
          <Link
            to="/ignis"
            className="inline-flex items-center gap-2 font-mono text-sm uppercase tracking-widest text-mist-muted transition-colors hover:text-mist"
          >
            Voltar ao IGNIS
          </Link>
        </div>
      </div>
    </div>
  );
}
